import { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from '@aimsos/i18n';
import { TextArea, Textfield } from '@aimsos/ui';

/** The prototype caps the description; enforce it in the control, not on save. */
const DESCRIPTION_MAX_LENGTH = 120;

/**
 * `NAME YOUR WIDGET` — the widget's identity (CT-5197).
 *
 * First block of the Configure tab since 2026-09-08: it holds the one field
 * that gates the save, so burying it under the type grid was backwards.
 *
 * Holds **no** validity logic — `canSave` lives in `useWidgetBuilderForm`. What
 * it decides is *when to surface* the required error: on blur, so typing the
 * first character of a name does not flash one.
 */
function WidgetIdentityFields({ name, description, onNameChange, onDescriptionChange }) {
  const { t } = useTranslation('dashboards');
  const [nameTouched, setNameTouched] = useState(false);

  const handleNameBlur = useCallback(() => setNameTouched(true), []);

  const handleDescriptionChange = useCallback(
    (event) => onDescriptionChange(event.target.value.slice(0, DESCRIPTION_MAX_LENGTH)),
    [onDescriptionChange],
  );

  const showNameError = nameTouched && name.trim().length === 0;

  return (
    <div className="flex flex-col gap-4">
      <Textfield
        aria-label={t('widgetBuilder.configure.nameLabel')}
        placeholder={t('widgetBuilder.configure.namePlaceholder')}
        value={name}
        onChange={onNameChange}
        onBlur={handleNameBlur}
        state={showNameError ? 'error' : 'default'}
        supportingText={showNameError ? t('widgetBuilder.configure.nameRequired') : ''}
        required
        aria-required="true"
        aria-invalid={showNameError || undefined}
      />

      <TextArea
        id="widget-description"
        aria-label={t('widgetBuilder.configure.descriptionLabel')}
        rows={2}
        value={description}
        maxLength={DESCRIPTION_MAX_LENGTH}
        onChange={handleDescriptionChange}
        placeholder={t('widgetBuilder.configure.descriptionPlaceholder')}
      />
    </div>
  );
}

WidgetIdentityFields.propTypes = {
  name: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  onNameChange: PropTypes.func.isRequired,
  onDescriptionChange: PropTypes.func.isRequired,
};

export default WidgetIdentityFields;
